/* eslint-disable sonarjs/cognitive-complexity */
/* eslint-disable max-len */
import { EventsHero } from '../blocks/EventsHero';
import ProductsGallery from '../blocks/ProductsGallery';
import { AboutForm } from '../forms/AboutForm';
import { InputType } from '../../types';
import { CTATileCarousel } from '../blocks/CTATileCarousel';
import LogoCardGrid from '../blocks/LogoCardGrid';
import { HalfImgHero } from '../blocks';
import MeetTheTeam from '../blocks/MeetTheTeam';
import { ModalForm } from '../forms/ModalForm';
import { useState } from 'react';

export const CannesLions2026Page = () => {
  // State for show/hide forms and setting their associated fields
  const [showFormModal, setShowFormModal] = useState(true);
  const [currentFormConfig, setCurrentFormConfig] = useState<(typeof formConfigs)[keyof typeof formConfigs] | null>(null);

  // Form configurations
  const formConfigs = {
    meetWithAdvertising: {
      heading: '<h3>Meet with<br/><strong>Xumo Advertising</strong></h3>',
      successMessage: 'Thank you for contacting us! Our team has received your inquiry and will connect with you after reviewing your submission.',
      source: 'meet-the-team',
      emailSubject: 'Cannes Lions 2026 - Connect with Xumo Advertising',
      collection: 'cannes_lions_2026_submissions',
    },
    freeWheelBeachParty: {
      heading: '<h3>Request an invite for<br/><strong>FreeWheel Beach Party</strong></h3>',
      successMessage: 'Thank you for contacting us! Our team has received your inquiry and will connect with you after reviewing your submission.',
      source: 'freewheel-beach-party',
      emailSubject: 'Cannes Lions 2026 - FreeWheel Beach Party Invite Request',
      collection: 'cannes_lions_2026_submissions',
    },
    soireeAtSea: {
      heading: '<h3>Request an invite for <br/><strong>Soiree at Sea</strong></h3>',
      successMessage: 'Thank you for contacting us! Our team has received your inquiry and will connect with you after reviewing your submission.',
      source: 'soiree-at-sea',
      emailSubject: 'Cannes Lions 2026 - Soiree at Sea Invite Request',
      collection: 'cannes_lions_2026_submissions',
    },
  };

  // Shared form fields for all forms
  const formFields = [
    { type: InputType.Name, name: 'first_name', placeholder: 'First name' },
    { type: InputType.Name, name: 'last_name', placeholder: 'Last name' },
    { type: InputType.Email, name: 'email', placeholder: 'Email' },
    { type: InputType.Name, name: 'company', placeholder: 'Company' },
    { type: InputType.Textarea, name: 'message', placeholder: 'Message', width: 'full' },
  ];

  // Helper to set correct form config
  const openForm = (configKey: keyof typeof formConfigs) => {
    setCurrentFormConfig(formConfigs[configKey]);
    setShowFormModal(true);
  };

  return (
    <>
      <EventsHero
        bgColor="xumoDarkPlum"
        textColor="white"
        eyebrow={'<span>Xumo</span> <span>at</span> <strong>Cannes</strong> <strong>Lions</strong> <strong>2026</strong>'}
        title={
          '<span>Reach</span>  <span>viewers</span>  <span>in</span>  <span>the</span>  <strong>moments</strong> <strong>that</strong> <strong>matter</strong></span>'
        }
        body={[
          {
            headingAlt: 'June 22–26',
            className: 'type-heading-alt weight-500',
          },
          {
            bodyBase: 'Cannes, France',
            className: 'type-subheading-xs mt-8',
          },
        ]}
        cta={[
          {
            ctaRow: [
              {
                label: 'Connect with Xumo at Cannes',
                color: 'xumoAltBerryLight',
                light: true,
                fullalways: true,
                onClick: () => openForm('meetWithAdvertising'),
              },
            ],
          },
        ]}
        imageSrc="/static/images/refresh/2026/cannes/cannes-26-hero.webp"
      />
      <MeetTheTeam
        bgColor={'xumoDarkPlum'}
        cardTextColor={'white'}
        headerContent={[
          {
            image: {
              src: '/static/images/refresh/2025/ces/1_XUMO_ADVERTISING_LOGO-Horizontal-FullColorReverse-RGB.png',
              alt: 'Xumo Advertising',
              width: 500,
              height: 41,
            },
            className: 'w-[351px] lg:w-[500px] h-auto',
          },
          { title: 'Where streaming gets smarter<br/>and <strong>advertising gets better</strong>', className: 'text-white mt-12 mb-12 lg:mt-16 lg:mb-16' },
          {
            bodyBase:
              'Streaming has never been bigger. It’s also never been more complicated. At Cannes Lions 2026, Xumo is showing what happens when streaming finally works for advertisers. We’re bringing together scale, simplicity and measurable impact – and we’d love to connect.',
            className: 'text-white mb-12',
          },
        ]}
        cta={[
          {
            ctaRow: [
              {
                label: 'Connect with Xumo at Cannes',
                color: 'xumoAltBerryLight',
                light: true,
                fullalways: true,
                onClick: () => openForm('meetWithAdvertising'),
              },
            ],
          },
        ]}
      />
      <CTATileCarousel
        bgColor="xumoDarkPlum"
        cardBgColor="xumoDarkPlum"
        center={false}
        bgImage="/static/images/refresh/2025/ces/Xumo_Swirl_Transparent_16k.png"
        header={[{ title: 'Xumo’s Cannes Lions lineup', className: 'text-white mb-12 md:mb-24 text-center mx-auto w-[82%] md:w-full' }]}
        tiles={[
          [
            { subheadingSmall: 'RSVP', className: 'text-white mb-2' },
            { headingAlt: 'Building What’s Next: The Developer Playbook', className: 'text-white' },
            { subheadingSmall: 'Monday | 4 PM | FreeWheel Beach', className: 'text-white mt-4' },
            {
              bodyBase:
                'In this session, Ying Wang, GM of Xumo Advertising, will join leaders on FreeWheel Beach to showcase how Xumo’s partnership with FreeWheel connects data, identity, and activation—transforming fragmented streaming into unified audiences and measurable results.',
              className: 'text-white max-w-[700px] mb-4',
            },
            {
              ctaRow: [
                {
                  href: 'https://www.freewheelcannes.com/',
                  label: 'Register Now',
                  color: 'xumoAltBerryLight',
                  light: true,
                  target: '_blank',
                  fullalways: true,
                },
              ],
            },
          ],
          [
            { subheadingSmall: 'RSVP', className: 'text-white mb-2' },
            { headingAlt: 'Fandom, Fragmentation & the Future of Sports', className: 'text-white' },
            { subheadingSmall: 'Tuesday | 4 PM | The Room Yacht', className: 'text-white mt-4' },
            {
              bodyBase:
                'From the explosive growth of women’s sports to the increasing value of long-tail sports, the sports ecosystem is creating new opportunities for brands to drive meaningful impact. Join leaders from Xumo, Spectrum Reach and The Collective for a dynamic panel discussion exploring what’s next for marketers, media, and growth.',
              className: 'text-white max-w-[700px] mb-4',
            },
            {
              ctaRow: [
                {
                  href: 'https://experience.spectrumreach.com/Cannes2026',
                  label: 'Register Now',
                  color: 'xumoAltBerryLight',
                  light: true,
                  target: '_blank',
                  fullalways: true,
                },
              ],
            },
          ],
          [
            { subheadingSmall: 'RSVP', className: 'text-white mb-2' },
            { headingAlt: 'Cocktails & Canapes', className: 'text-white' },
            { subheadingSmall: 'Monday – Wednesday | 5–7 PM', className: 'text-white mt-4' },
            {
              bodyBase: 'Join us for twilight drinks and bites as the sun sets over the Port de Cannes.',
              className: 'text-white max-w-[700px] mb-4',
            },
            {
              ctaRow: [
                {
                  href: 'https://docs.google.com/forms/d/e/1FAIpQLSeao8yvIrXL4VG8uBh1gFTGaO9-4lIqGJXvztTF7Wi7Ly_Wtg/viewform',
                  label: 'RSVP',
                  color: 'xumoAltBerryLight',
                  light: true,
                  target: '_blank',
                  fullalways: true,
                },
              ],
            },
          ],
          [
            { subheadingSmall: 'RSVP', className: 'text-white mb-2' },
            { headingAlt: 'Late Night', className: 'text-white' },
            { subheadingSmall: 'Monday – Wednesday | 10 PM – 2 AM', className: 'text-white mt-4' },
            {
              bodyBase: 'Relax, connect, and unwind away from the bustle of the Croisette.',
              className: 'text-white max-w-[700px] mb-4',
            },
            {
              ctaRow: [
                {
                  href: 'https://docs.google.com/forms/d/e/1FAIpQLSeao8yvIrXL4VG8uBh1gFTGaO9-4lIqGJXvztTF7Wi7Ly_Wtg/viewform',
                  label: 'RSVP',
                  color: 'xumoAltBerryLight',
                  light: true,
                  target: '_blank',
                  fullalways: true,
                },
              ],
            },
          ],
          [
            { subheadingSmall: 'RSVP', className: 'text-white mb-2' },
            { headingAlt: 'Happy Hour with Universal Ads', className: 'text-white' },
            { subheadingSmall: 'Monday | 5 – 8&#58;30 PM | FreeWheel Beach', className: 'text-white mt-4' },
            {
              bodyBase: 'Join Xumo and Universal Ads as we kick off the week at FreeWheel Beach.',
              className: 'text-white max-w-[700px] mb-4',
            },
            {
              ctaRow: [
                {
                  href: 'https://www.freewheelcannes.com/',
                  label: 'Register Now',
                  color: 'xumoAltBerryLight',
                  light: true,
                  target: '_blank',
                  fullalways: true,
                },
              ],
            },
          ],
          [
            { subheadingSmall: 'Invite only', className: 'text-white mb-2' },
            { headingAlt: 'Soiree at Sea', className: 'text-white' },
            { subheadingSmall: 'Thursday | 9&#58;30 AM', className: 'text-white mt-4' },
            {
              bodyBase: 'Spend the day on land and at sea as a guest of Xumo.',
              className: 'text-white max-w-[700px] mb-4',
            },
            {
              ctaRow: [
                {
                  href: 'https://docs.google.com/forms/d/e/1FAIpQLSfy9QuBsdK8P3T0QGFq4vLaI4qkODa9iagpW3kGtsfxoQc0Dw/viewform',
                  label: 'Request an invite',
                  color: 'xumoAltBerryLight',
                  light: true,
                  fullalways: true,
                  onClick: () => openForm('soireeAtSea'),
                },
              ],
            },
          ],
        ]}
      />
      <HalfImgHero
        variant="vertical"
        bgimg="/static/images/refresh/2026/cannes/cannes-map.webp"
        bgColor="xumoDarkPlum"
        copyPadding={false}
        center={true}
        content={[
          {
            title: 'Where to <strong>find us</strong>',
            className: 'text-white text-center -mb-8 lg:-mb-0',
          },
          {
            body: 'The Room Yacht, Port de Cannes, Jetée Albert Edouard',
            className: 'text-white text-center',
          },
        ]}
        nascarLogo={false}
        bannerActive={false}
      />
      <LogoCardGrid
        variant="text"
        stackedContent={[
          {
            heading: 'The <strong>next era</strong> of<br/>reaching streaming audiences',
            className: 'text-white mb-0 md:mb-8',
          },
          {
            bodyBase:
              'Advertising is at an inflection point. Audiences want more relevance. Advertisers want less fragmentation. Everyone wants more simplicity. Xumo’s proprietary data solutions help brands reach audiences in the moments that matter most — from the first screen to premium content to major live moments. Backed by Comcast and Charter, we power a streaming ecosystem designed to help advertisers connect with audiences all in one place where attention is highest and impact is measurable.',
            className: 'text-white text-[18px] md:text-[20px]',
          },
        ]}
        logoCards={[
          {
            title: 'Entertainment, sports & culture. Connected.',
            body: 'We bring together the content and moments audiences care about most.',
          },
          {
            title: 'One buy.<br/>Every screen.<br/>Less complexity.',
            body: 'We make it easy for advertisers to reach viewers across the streaming ecosystem.',
          },
          {
            title: 'Show up when attention is highest.',
            body: 'Reach audiences across the Home Screen, live moments and premium FAST content.',
          },
          {
            title: 'Prove that it worked.',
            body: 'Turn your investments into impact, action and outcomes.',
          },
        ]}
      />

      {/* Single modal with dynamic content */}
      {currentFormConfig && (
        <ModalForm
          isDark={true}
          isOpen={showFormModal}
          onClose={() => setShowFormModal(false)}
          heading={currentFormConfig.heading}
          formFields={formFields}
          CTA={{ label: 'Submit' }}
          formAction={{
            submissionEndpoint: `/api/contact?email_subject=${encodeURIComponent(currentFormConfig.emailSubject)}&collection=${currentFormConfig.collection}`,
            onSuccess: () => setShowFormModal(false),
            successMessage: currentFormConfig.successMessage,
            formType: 'event',
          }}
          styleOverrides={{
            form: 'mt-16 gap-2',
            formField: 'rounded-none outline-[0.5px] bg-transparent outline-[#FFFFFF] placeholder:text-white',
            hideLabel: true,
            CTA: { alignment: 'w-full md:w-fit self-center mt-6 bg-[#FFFFFF] text-[#000000]' },
            accentColor: 'xumoAltBerryLight',
            hoverColor: 'xumoAltBerryDark',
          }}
        />
      )}
    </>
  );
};
